import {
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
  NO_ERRORS_SCHEMA,
  Optional,
} from '@angular/core';
import {
  BrowserModule,
  EVENT_MANAGER_PLUGINS,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { HttpClient, HttpClientModule } from '@angular/common/http';
import { NxModule } from '@nrwl/angular';
import { environment } from '../environments/environment';

import {
  UniqueInputEventPlugin,
  UNIQUE_INPUT_EVENT_PLUGIN_CONFIG,
  UtilsModule,
} from '@biz2x/libraries/core-utils/utils';

import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';

import { UiKitModule } from '@biz2x/libraries/core-utils/ui-kit';
import { SharedLazyModule } from '@biz2x/libraries/core-utils/shared-lazy';

import { StoreModule, MetaReducer, META_REDUCERS } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as fromAppState from './+state/app-state.reducer';
import { AppStateEffects } from './+state/app-state.effects';
import { AppStateFacade } from './+state/app-state.facade';
import { updateRootState } from './+state/app-meta.reducer';
import { FormsModule } from '@angular/forms';
import { CONSTANTS } from '../assets/base/constants/constant';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { DashboardWidgetMainModule } from 'dashboard-widget';

declare function require(name: string);

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, `/assets/i18n/`, '.json');
}

export function getCssJson() {
  return require(`../assets/${environment.client_folder}/json/css.json`);
}

export function getEnJson() {
  return require(`../assets/${environment.client_folder}/i18n/en.json`);
}

export function getRoute() {
  return require(`../assets/${environment.client_folder}/json/routes.json`);
}

export function getproductConfigJson() {
  return require(`../assets/${environment.client_folder}/json/product-config.json`);
}

const ERROR_MESSAGES = require(`../assets/${environment.client_folder}/json/error-messages.json`);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    DashboardWidgetMainModule,
    FormsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    UiKitModule,
    SharedLazyModule,
    NxModule.forRoot(),
    HttpClientModule,
    StoreModule.forRoot(
      { [fromAppState.APP_STATE_FEATURE_KEY]: fromAppState.reducer },
      { metaReducers: [updateRootState] }
    ),
    EffectsModule.forRoot([AppStateEffects]),
    StoreDevtoolsModule.instrument(),
    UtilsModule.forRoot(environment, ERROR_MESSAGES),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideMessaging(() => getMessaging()),
  ],
  providers: [
    { provide: 'environment', useValue: environment },
    { provide: 'cssList', useFactory: getCssJson },
    { provide: 'APP_ROUTES', useFactory: getRoute },
    { provide: 'enJson', useFactory: getEnJson },
    {
      provide: 'constants',
      useValue: CONSTANTS,
    },
    { provide: 'CONSTANTS', useValue: CONSTANTS },
    { provide: 'productConfig', useFactory: getproductConfigJson },
    window.navigator['msSaveOrOpenBlob']
      ? { provide: UNIQUE_INPUT_EVENT_PLUGIN_CONFIG, useValue: {} }
      : [],
    window.navigator['msSaveOrOpenBlob']
      ? {
          provide: EVENT_MANAGER_PLUGINS,
          useClass: UniqueInputEventPlugin,
          multi: true,
          deps: [[new Optional(), UNIQUE_INPUT_EVENT_PLUGIN_CONFIG]],
        }
      : [],
    AppStateFacade,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private readonly translateService: TranslateService) {
    this.translateService.setDefaultLang('en');
  }
}
